import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'

interface ModalProps {
    open: boolean
    onClose: () => void
    label: string
    className?: string
    children: ReactNode
}

/**
 * A native <dialog> shown as a modal: it traps focus, closes on Escape and on a click outside
 * the panel, and makes the rest of the page inert while open.
 */
export const Modal: FC<ModalProps> = ({ open, onClose, label, className, children }): ReactNode => {
    const ref = useRef<HTMLDialogElement>(null)
    const onCloseRef = useRef(onClose)

    onCloseRef.current = onClose

    useEffect(() => {
        const dialog = ref.current

        if (!dialog) return

        if (open && !dialog.open) {
            try {
                dialog.showModal()
            }
            catch {
                dialog.setAttribute('open', '')
            }

            document.documentElement.style.overflow = 'hidden'
        }

        if (!open && dialog.open) dialog.close()

        if (!open) document.documentElement.style.overflow = ''
    }, [open])

    useEffect(() => {
        const dialog = ref.current

        if (!dialog) return

        const handleCancel = (event: Event) => {
            event.preventDefault()
            onCloseRef.current()
        }

        const handleClick = (event: MouseEvent) => {
            if (event.target === dialog) onCloseRef.current()
        }

        dialog.addEventListener('cancel', handleCancel)
        dialog.addEventListener('click', handleClick)

        return () => {
            dialog.removeEventListener('cancel', handleCancel)
            dialog.removeEventListener('click', handleClick)
            document.documentElement.style.overflow = ''
        }
    }, [])

    return (
        <dialog
            ref={ref}
            aria-label={label}
            className={cn(
                'm-0 max-h-none max-w-none bg-transparent p-0 text-foreground backdrop:bg-obsidian/75 backdrop:backdrop-blur-sm',
                'open:animate-in open:fade-in open:duration-300',
                className
            )}
        >
            {open ? children : null}
        </dialog>
    )
}
